import { useState } from 'react';

import PropTypes from 'prop-types';

import { FormattedMessage } from '@zenbusiness/application-commons-intl';
import Button from '@zenbusiness/zui/Button';
import Chip from '@zenbusiness/zui/Chip';
import TextField from '@zenbusiness/zui/TextField';

import useFlowState from '#client/hooks/flow/useFlowState';

import * as Styled from './MultipleJurisdictionsSelection.style';

const MultipleJurisdictionsSelection = ({
  options,
  selectedValues,
  primaryAction,
  showFieldError,
  updateFieldError
}) => {
  const {
    businessInfo: { jurisdictionState }
  } = useFlowState();
  const [clickedJurisdictions, setClickedJurisdictions] = useState([]);
  const [currentSelectedValues, setCurrentSelectedValues] = useState(selectedValues);
  const hasMaxLimit = currentSelectedValues.length > 10;
  const isOnlyDomJurSelected =
    currentSelectedValues.length === 1 && currentSelectedValues[0].value === jurisdictionState;

  const handleChange = (event, value) => {
    setClickedJurisdictions(value);
    setCurrentSelectedValues(value);
    if (value.length > 0) {
      updateFieldError(false);
    } else {
      updateFieldError(true);
    }
  };

  return (
    <Styled.Container>
      <Styled.MultipleChoice
        data-testid="multiple-jurisdiction"
        defaultValue={currentSelectedValues || null}
        filterSelectedOptions
        getOptionLabel={(option) => option.label}
        id="tags-outlined"
        multiple
        options={options}
        renderInput={(params) => (
          <TextField
            {...params}
            error={showFieldError || hasMaxLimit}
            helperText={
              showFieldError || hasMaxLimit ? (
                <FormattedMessage
                  id={
                    hasMaxLimit
                      ? 'shopping.page.multiStateRegisteredAgent.multiplechoice.field.error.maxLimit'
                      : 'shopping.page.multiStateRegisteredAgent.multiplechoice.field.error.required'
                  }
                />
              ) : null
            }
            label={
              <FormattedMessage id="shopping.page.multiStateRegisteredAgent.placeholder.label" />
            }
          />
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.value}
              disabled={option.value === jurisdictionState && isOnlyDomJurSelected}
              label={option.label}
              size="small"
              sx={{ margin: '2px' }}
            />
          ))
        }
        size="small"
        sx={{ flexGrow: '1' }}
        value={
          options.filter(
            (option) =>
              currentSelectedValues.some((selectedValue) => option.value === selectedValue.value) ||
              clickedJurisdictions.some((jurisdiction) => option.value === jurisdiction.value)
          ) ?? null
        }
        onChange={handleChange}
      />
      <Styled.CtaContainer>
        <Styled.Actions>
          <Button
            clickstreamProps={primaryAction.clickstreamProps}
            color="primary"
            data-testid="multiple-jurisdiction-submit"
            disabled={showFieldError || hasMaxLimit}
            fullWidth
            loading={primaryAction.loading}
            type="submit"
            variant="contained"
            onClick={() => primaryAction.onClick(clickedJurisdictions)}
          >
            {primaryAction.label}
          </Button>
        </Styled.Actions>
      </Styled.CtaContainer>
    </Styled.Container>
  );
};

MultipleJurisdictionsSelection.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      assetPath: PropTypes.string.isRequired
    })
  ).isRequired,
  selectedValues: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      assetPath: PropTypes.string.isRequired
    })
  ),
  primaryAction: PropTypes.shape({
    loading: PropTypes.bool,
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
    clickstreamProps: PropTypes.shape({ elementName: PropTypes.string.isRequired }).isRequired
  }),
  showFieldError: PropTypes.bool,
  updateFieldError: PropTypes.func
};

export default MultipleJurisdictionsSelection;
